import { HttpClient } from '@angular/common/http';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { catchError, map, Observable, of, switchMap, timer } from 'rxjs';
import { CheckPodDuplicatoGQL } from '../../_graphql/generated';
import { LoadingService } from '../../_services/loading.service';
import { GseService } from '../../_services/gse';
import { toPgTextArray } from '../../_utils/pg-text-array';

export interface PodDuplicatoValidatorOptions {
  getter: CheckPodDuplicatoGQL;
  role?: string;
  // funzioni e non valori: vanno lette al momento della validazione (la tipologia può cambiare dopo aver scritto il POD)
  tipologia: () => string | null | undefined;
  excludeId?: () => string | null | undefined;
  // staging: controlla anche le pratiche in lavorazione, escludendo la riga di staging corrente
  includeStaging?: boolean;
  excludeStagingId?: () => string | null | undefined;
  // notifica l'esito (con il POD verificato, normalizzato) appena la verifica termina: il campo non emette un evento
  // quando finisce il controllo asincrono
  onEsito?: (esito: 'ok' | 'duplicato' | 'errore', pod: string) => void;
}

export function podValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = (control.value || '').trim();

  return !value || /^IT\d{3}E[0-9A-Z]{8,9}$/.test(value.toUpperCase())
    ? null
    : { pod: true };
}

export function podValidatorGSE(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: any,
): any {
  const value = (control.value || '').trim();
  if (!value) return of(null);

  if (value.toUpperCase() === 'IT000E00000000' || value.toUpperCase().startsWith('IT017E'))
    return of(null);

  const loading = options.loading as any;
  const gse = options.gse as GseService;

  const coordinates = options?.point?.coordinates;
  if (
    !Array.isArray(coordinates) ||
    coordinates.length < 2 ||
    !Number.isFinite(Number(coordinates[0])) ||
    !Number.isFinite(Number(coordinates[1]))
  ) {
    // Non bloccare il form se il punto non e' disponibile:
    // la validazione POD prosegue con le altre regole.
    return of(null);
  }

  const [lon, lat] = coordinates;
  const coord = `${lon},${lat}`;
  setTimeout(() => loading.start());

  return gse.checkCoord(coord).pipe(
    switchMap((resCodAc: any) => {
      const cod_ac = resCodAc?.features?.[0]?.attributes?.COD_AC;

      if (!cod_ac) {
        loading.stop();
        return of({ podGSE: true }); // errore: non trovato
      }

      return gse.checkPod(value).pipe(
        map((resPod: any) => {
          loading.stop();

          const valid = resPod.features?.some((el: any) => {
            const value = el.attributes?.COD_AC;
            if (!value) return { podGSE: true };

            return Array.isArray(cod_ac)
              ? cod_ac.includes(value)
              : value === cod_ac;
          });

          return valid ? null : { podGSECheck: true };
        }),
        catchError(() => {
          loading.stop();
          return of({ podGSE: true });
        }),
      );
    }),
    catchError(() => {
      loading.stop();
      return of({ podGSE: true });
    }),
  );
}

// Un POD non può stare su due sottoscrizioni non cancellate con la stessa tipologia richiesta
export function podDuplicatoValidator(
  control: AbstractControl,
  field: FormlyFieldConfig,
  formlyOptions?: { [id: string]: unknown },
): Observable<ValidationErrors | null> {
  // formly tipizza le options come mappa generica
  const options = formlyOptions as unknown as PodDuplicatoValidatorOptions;
  const value = (control.value || '').trim().toUpperCase();
  const esito = (
    errori: ValidationErrors | null,
  ): ValidationErrors | null => {
    options.onEsito?.(
      errori?.['podDuplicato']
        ? 'duplicato'
        : errori?.['podDuplicatoCheck']
          ? 'errore'
          : 'ok',
      value,
    );
    return errori;
  };
  if (!value || value === 'IT000E00000000') return of(esito(null));

  const tipologia = options.tipologia();
  if (!tipologia) return of(esito(null));

  // debounce: a ogni tasto Angular annulla la sottoscrizione precedente
  return timer(400).pipe(
    switchMap(() =>
      options.getter.fetch(
        {
          pods: toPgTextArray([value]),
          tipologia,
          exclude_id: options.excludeId?.() ?? null,
          include_staging: options.includeStaging ?? false,
          exclude_staging_id: options.excludeStagingId?.() ?? null,
        },
        {
          fetchPolicy: 'network-only',
          context: {
            headers: { 'x-hasura-role': options.role ?? 'anonymous' },
          },
        },
      ),
    ),
    map((res) => {
      // ⚠️ errorPolicy 'all' (app.config.ts): gli errori GraphQL non lanciano, arrivano in res.errors senza data
      if (res.errors?.length || !res.data) {
        return esito({ podDuplicatoCheck: true });
      }
      return esito(
        res.data.check_pod_duplicato.length > 0 ? { podDuplicato: true } : null,
      );
    }),
    catchError(() => of(esito({ podDuplicatoCheck: true }))),
  );
}

export function podValidatorGSEExists(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: any,
): any {
  const value = (control.value || '').trim();
  if (!value) return of(null);

  if (value.toUpperCase() === 'IT000E00000000' || value.toUpperCase().startsWith('IT017E'))
    return of(null);

  const loading = options.loading as any;
  const gse = options.gse as GseService;

  return gse.checkPod(value).pipe(
    map((resPod: any) => {
      const valid = resPod.features?.length > 0;

      return valid ? null : { podGSEExists: true };
    }),
    catchError(() => {
      return of({ podGSEExists: true });
    }),
  );
}
